import { Fragment, forwardRef } from "react";

import { classNames } from "@oneleet/utils/src/utils";

import type { Language } from "prism-react-renderer";
import { Highlight, themes } from "prism-react-renderer";
import { twMerge } from "tailwind-merge";

import { CopyButton } from "../..";

const theme = themes.nightOwl;

type Props = {
  className?: string;
  children: string;
  language: Language;
  showDownloadButton?: boolean;
  filename?: string;
};

const Fence = forwardRef<HTMLPreElement, Props>(
  ({ children, language, className, showDownloadButton, filename }, ref) => {
    return (
      <div className={twMerge("my-2", className)}>
        <Highlight
          code={children ? children.trim() : ""}
          language={language || ""}
          theme={theme}
        >
          {({ className, style, tokens, getTokenProps }) => {
            const plainTextContent = tokens
              .map((line) => line.map((token) => token.content).join(""))
              .join("\n");

            const handleDownload = () => {
              const blob = new Blob([plainTextContent], {
                type: "text/plain;charset=utf-8",
              });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = filename || `code.${language || "txt"}`;
              link.click();
              URL.revokeObjectURL(url);
            };

            return (
              <pre
                ref={ref}
                className={classNames(
                  className,
                  "relative overflow-x-auto rounded-md p-4 text-sm",
                  showDownloadButton ? "pt-6" : "pt-4",
                )}
                style={style}
              >
                <code className="block overflow-x-auto text-sm">
                  {tokens.map((line, lineIndex) => (
                    <Fragment key={lineIndex}>
                      {line
                        .filter((token) => !token.empty)
                        .map((token, tokenIndex) => (
                          <span
                            key={tokenIndex}
                            {...getTokenProps({ token })}
                          />
                        ))}
                      {"\n"}
                    </Fragment>
                  ))}
                </code>
                <div className="absolute right-2 top-2 flex space-x-2">
                  {showDownloadButton && (
                    <button
                      type="button" // Added type="button" to prevent form submission
                      onClick={handleDownload}
                      className="rounded border border-gray-300 bg-transparent px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 focus:outline-none"
                    >
                      Download
                    </button>
                  )}
                  <CopyButton content={plainTextContent} />
                </div>
              </pre>
            );
          }}
        </Highlight>
      </div>
    );
  },
);

Fence.displayName = "Fence";

export default Fence;
